import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { isItemOrCombo } from 'features/cart/utils';
import { mapMenuSelectionsToICartEntry } from 'features/cart/utils/minimal-cart';

import {
  IConfigOfferEntryDto,
  IGetUserMinimalOrdersQuery,
  IMenuSelectionInput,
  IRewardEntryDto,
  ISystemWideOfferEntryDto,
} from 'generated/graphql-gateway';
import { isCartEntryReorderable } from 'hooks/use-reorderable-orders/utils';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';
import { routes } from 'utils/routing';

import { MinimalRecentOrderData } from './types';

type MinimalOrderQueryItem = NonNullable<
  NonNullable<IGetUserMinimalOrdersQuery['userMinimalOrders']['orders']>[number]
>;

type MinimalOrderSelectionNode = {
  id: string;
  quantity: number;
  menuSelections?: ReadonlyArray<MinimalOrderSelectionNode> | null;
};

const mapCartEntriesToMenuSelections = (entries: ICartEntry[]): IMenuSelectionInput[] => {
  return entries.map(entry => ({
    id: entry.id,
    quantity: entry.quantity,
    menuSelections: mapCartEntriesToMenuSelections(entry.children ?? []),
  }));
};

const collectBenefitEntries = (entries: ICartEntry[]): ICartEntry[] => {
  return entries.flatMap(entry => {
    if (isItemOrCombo(entry)) {
      return [entry];
    }

    return collectBenefitEntries(entry.children ?? []);
  });
};

const remapStandaloneBenefitEntries = (
  entries: ICartEntry[],
  options: {
    menu: IndexedMenu<ILocalizedMenuEntry>;
    indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto>;
    indexedRewards: IndexedMenu<IRewardEntryDto>;
    posServiceMode: 'delivery' | 'pickup';
  }
): ICartEntry[] => {
  if (!entries.length) {
    return [];
  }

  const standaloneSelections = mapCartEntriesToMenuSelections(entries);

  return mapMenuSelectionsToICartEntry(standaloneSelections, {
    ...options,
    url: routes.menu,
  }).filter(entry => isCartEntryReorderable(entry));
};

const mapEntryToReorderableEntries = (
  entry: ICartEntry,
  options: {
    menu: IndexedMenu<ILocalizedMenuEntry>;
    indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto>;
    indexedRewards: IndexedMenu<IRewardEntryDto>;
    posServiceMode: 'delivery' | 'pickup';
  }
): ICartEntry[] => {
  switch (entry.type) {
    case MinimalCartEntryType.reward: {
      const [benefitEntry] = collectBenefitEntries(entry.children ?? []);
      return benefitEntry ? remapStandaloneBenefitEntries([benefitEntry], options) : [];
    }
    case MinimalCartEntryType.incentive:
    case MinimalCartEntryType.discount:
      return remapStandaloneBenefitEntries(collectBenefitEntries(entry.children ?? []), options);
    case MinimalCartEntryType.donation:
    case MinimalCartEntryType.extra:
      return [];
    default:
      return isCartEntryReorderable(entry) ? [entry] : [];
  }
};

const mapMinimalOrderNodesToMenuSelections = (
  entries?: ReadonlyArray<MinimalOrderSelectionNode> | null
): IMenuSelectionInput[] => {
  if (!entries?.length) {
    return [];
  }

  return entries.map(entry => ({
    id: entry.id,
    quantity: entry.quantity,
    menuSelections: mapMinimalOrderNodesToMenuSelections(entry.menuSelections),
  }));
};

interface MapMinimalOrderToCardDataArgs {
  order: MinimalOrderQueryItem;
  menu: IndexedMenu<ILocalizedMenuEntry>;
  indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto>;
  indexedRewards: IndexedMenu<IRewardEntryDto>;
  posServiceMode: 'delivery' | 'pickup';
}

export const mapMinimalOrderToCardData = ({
  order,
  menu,
  indexedOffers,
  indexedRewards,
  posServiceMode,
}: MapMinimalOrderToCardDataArgs): MinimalRecentOrderData => {
  const menuSelections = mapMinimalOrderNodesToMenuSelections(
    order.cart.menuSelections as ReadonlyArray<MinimalOrderSelectionNode>
  );

  const mappedEntries = mapMenuSelectionsToICartEntry(menuSelections, {
    menu,
    indexedOffers,
    indexedRewards,
    posServiceMode,
    url: routes.menu,
  });
  const minimalEntries = mappedEntries.flatMap((entry: ICartEntry) =>
    mapEntryToReorderableEntries(entry, {
      menu,
      indexedOffers,
      indexedRewards,
      posServiceMode,
    })
  );

  return {
    id: order._id,
    rbiOrderId: order.rbiOrderId,
    createdAt: order.createdAt,
    orderNumberFormatted: order.orderNumberFormatted,
    menuSelections,
    minimalEntries,
  };
};
